import React, { useState, useRef, useEffect } from 'react';
import { Play, Volume2, ArrowRight } from 'lucide-react';

// Splash plays a sequence of videos back-to-back, then calls onComplete.
const VIDEO_PLAYLIST = [
  {
    src: 'https://customer-assets.emergentagent.com/job_e8d2b5f5-0cb7-4419-b7f1-41d20d77a12b/artifacts/h1pdtdba_IMG_3290.MOV',
    type: 'video/mp4',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/40h4ropw_VID_20260121_104506_190.mp4',
    type: 'video/mp4',
  },
];

const SplashScreen = ({ onComplete }) => {
  const videoRef = useRef(null);
  const [videoIndex, setVideoIndex] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [needsInteraction, setNeedsInteraction] = useState(false);

  // Try to autoplay (unmuted, then fall back to muted if blocked by the browser).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => setIsPlaying(true))
        .catch(() => {
          video.muted = true;
          video.play()
            .then(() => {
              setIsPlaying(true);
              setNeedsInteraction(true);
            })
            .catch(() => setNeedsInteraction(true));
        });
    }
  }, []);

  // When the playlist index advances, autoplay the next clip (keep current mute state).
  useEffect(() => {
    if (videoIndex === 0) return;
    const video = videoRef.current;
    if (!video) return;
    video.load();
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // If autoplay of the next clip is blocked, fall back to muted.
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  }, [videoIndex]);

  const handleUnmute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      setNeedsInteraction(false);
    }
  };

  const handleStart = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      video.currentTime = 0;
      video.play().then(() => {
        setIsPlaying(true);
        setNeedsInteraction(false);
      }).catch(() => {});
    }
  };

  // When a clip ends: advance to the next clip, or finish the splash if it was the last.
  const handleVideoEnd = () => {
    if (videoIndex < VIDEO_PLAYLIST.length - 1) {
      setVideoIndex((i) => i + 1);
      return;
    }
    setIsEnding(true);
    setTimeout(() => onComplete(), 800);
  };

  const handleSkip = () => {
    setIsEnding(true);
    setTimeout(() => onComplete(), 300);
  };

  const currentVideo = VIDEO_PLAYLIST[videoIndex];

  return (
    <div 
      style={{ fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif' }}
      className={`fixed inset-0 z-[9999] bg-[#0a0a0a] flex items-center justify-center transition-opacity duration-700 ${
        isEnding ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Video Container */}
      <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
        <video
          ref={videoRef}
          key={currentVideo.src}
          data-testid={`splash-video-${videoIndex}`}
          className="h-full object-contain"
          style={{ aspectRatio: '9/16', maxWidth: '100vw' }}
          playsInline
          preload="auto"
          onEnded={handleVideoEnd}
        >
          <source src={currentVideo.src} type={currentVideo.type} />
        </video>

        {/* Tap for Sound - when playing muted */}
        {isPlaying && needsInteraction && (
          <button
            onClick={handleUnmute}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-md px-8 py-5 rounded-2xl flex items-center gap-4 hover:bg-black/70 transition-all border border-white/10"
          >
            <Volume2 className="w-7 h-7 text-white" />
            <span className="text-white font-bold text-base tracking-wide">Appuyez pour le son</span>
          </button>
        )}

        {/* Click to Start Overlay */}
        {!isPlaying && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a0a0a]">
            <img 
              src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg"
              alt="Talitha Koum"
              className="h-28 w-auto mb-10"
            />
            
            <button
              onClick={handleStart}
              data-testid="splash-play-button"
              className="group flex flex-col items-center gap-5"
            >
              <div className="w-24 h-24 bg-[#CD0000] rounded-full flex items-center justify-center group-hover:bg-[#c91560] transition-all group-hover:scale-110 shadow-[0_0_60px_rgba(228,25,107,0.3)]">
                <Play className="w-12 h-12 text-white ml-1.5" fill="white" />
              </div>
              <div className="flex items-center gap-2 text-white/80 font-bold text-sm tracking-widest uppercase mt-1">
                <Volume2 className="w-5 h-5" />
                <span>Appuyez pour regarder</span>
              </div>
            </button>
            
            <p className="text-white/30 text-xs mt-12 tracking-widest uppercase">
              Bienvenue chez TALITHA KOUMI TUG
            </p>
          </div>
        )}
      </div>

      {/* Skip Button - Large, visible, professional */}
      <button
        onClick={handleSkip}
        data-testid="splash-skip-button"
        className="absolute bottom-8 right-8 z-20 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/20 hover:border-white/40 text-white px-6 py-3 rounded-full font-bold text-sm tracking-wider transition-all flex items-center gap-3 group"
      >
        <span className="uppercase">{isPlaying ? 'Passer' : 'Passer la video'}</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </button>

      {/* Bottom brand */}
      <div className="absolute bottom-8 left-8 text-white/25 text-xs tracking-[0.2em] uppercase z-10 font-bold">
        TALITHA KOUMI TUG
      </div>
    </div>
  );
};

export default SplashScreen;
